import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { carsApi, Car, CarWithLoadedModel, CarsApiResponse, CarsParams } from '../api/carsApi';
import { carModelsApi } from '@/features/car-models/api/carModelsApi';
import { queryKeys, invalidateEntityQueries } from '@/shared/lib/react-query';

// Deprecated: используйте queryKeys.cars вместо этого
export const CARS_QUERY_KEY = ['cars'];
export const CARS_PER_PAGE = 10;

interface CarsResponse {
  data: CarWithLoadedModel[];
  total: number;
  isLoading: boolean;
  error: unknown;
}

/**
 * Получить список машин с загруженными моделями
 * Использует оптимизированную загрузку моделей через useQueries
 */
export const useCars = (params: CarsParams = {}): CarsResponse => {
  const carsQuery = useQuery({
    queryKey: queryKeys.cars.list(params),
    queryFn: () => carsApi.getCars(params),
    select: (response) => ({
      data: response.data || [],
      total: response.total || 0
    })
  });

  // Получаем уникальные ID моделей для оптимизации запросов
  const modelIds = carsQuery.data?.data?.map(car => car.model) ?? [];
  const uniqueModelIds = Array.from(new Set(modelIds));

  // Параллельно загружаем все модели
  const modelQueries = useQueries({
    queries: uniqueModelIds.map((id: string) => ({
      queryKey: queryKeys.carModels.detail(id),
      queryFn: () => carModelsApi.getCarModelById(id),
      staleTime: 5 * 60 * 1000, // Кэшируем на 5 минут
    }))
  });

  const isLoading = carsQuery.isLoading || modelQueries.some(q => q.isLoading);
  const error = carsQuery.error || modelQueries.find(q => q.error)?.error;

  // Создаем Map для быстрого поиска моделей
  const modelsMap = new Map(
    modelQueries
      .filter(q => q.data)
      .map((q, index) => [uniqueModelIds[index], q.data!])
  );

  // Собираем машины с моделями
  const carsWithLoadedModels: CarWithLoadedModel[] = carsQuery.data?.data?.map((car: Car) => ({
    ...car,
    model: modelsMap.get(car.model)!,
    busy_days: 0,
    free_days: 0
  })) ?? [];

  return {
    data: carsWithLoadedModels,
    total: carsQuery.data?.total ?? 0,
    isLoading,
    error
  };
};

export const useCreateCar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: carsApi.createCar,
    onSuccess: () => {
      invalidateEntityQueries.cars(queryClient);
    },
  });
};

export function useUpdateCar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Car> }) =>
      carsApi.updateCar(id, data),
    onSuccess: (_, { id }) => {
      // Инвалидируем конкретную машину и весь список
      queryClient.invalidateQueries({ queryKey: queryKeys.cars.detail(id) });
      invalidateEntityQueries.cars(queryClient);
    },
  });
}

/**
 * Получить машину по ID с загруженной моделью
 */
export function useCar(id: string) {
  const carQuery = useQuery({
    queryKey: queryKeys.cars.detail(id),
    queryFn: () => carsApi.getCarById(id)
  });

  const modelQuery = useQuery({
    queryKey: queryKeys.carModels.detail(carQuery.data?.model ?? ''),
    queryFn: () => carModelsApi.getCarModelById(carQuery.data?.model ?? ''),
    enabled: !!carQuery.data?.model
  });

  const data: CarWithLoadedModel | null = carQuery.data && modelQuery.data ? {
    ...carQuery.data,
    model: modelQuery.data,
    busy_days: 0,
    free_days: 0
  } : null;

  return {
    data,
    isLoading: carQuery.isLoading || modelQuery.isLoading,
    error: carQuery.error || modelQuery.error
  };
}

export function useUploadCarImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      carsApi.uploadCarImage(id, file),
    onSuccess: (_, { id }) => {
      // Инвалидируем конкретную машину и весь список
      queryClient.invalidateQueries({ queryKey: queryKeys.cars.detail(id) });
      invalidateEntityQueries.cars(queryClient);
    },
  });
}

/**
 * Загрузить несколько моделей машин параллельно
 * @deprecated Используйте напрямую queryKeys.carModels.detail() с useQueries
 */
export function useCarModels(modelIds: string[]) {
  return useQueries({
    queries: modelIds.map(id => ({
      queryKey: queryKeys.carModels.detail(id),
      queryFn: () => carModelsApi.getCarModelById(id),
      staleTime: 5 * 60 * 1000,
    }))
  });
}

export function useDeleteCar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => carsApi.deleteCar(id),
    onSuccess: () => {
      invalidateEntityQueries.cars(queryClient);
    },
  });
}

export function useCarEnums() {
  return useQuery({
    queryKey: queryKeys.cars.enums(),
    queryFn: () => carsApi.getEnums(),
    staleTime: 10 * 60 * 1000, // Кэшируем enums на 10 минут
  });
}
